import { Eye, Lock, GitPullRequest, ShieldCheck } from "lucide-react";
import { Link } from "@inertiajs/react";

const SECURITY_POINTS = [
  {
    icon: Eye,
    title: "Read-only AWS access",
    description:
      "Infralyst connects via a Terraform-created IAM role with read-only permissions, so it can see resources and utilization without you managing long-lived access keys.",
  },
  {
    icon: Lock,
    title: "Minimal permissions",
    description:
      "We follow the principle of least privilege and only request the AWS APIs needed to analyze usage and propose changes.",
  },
  {
    icon: GitPullRequest,
    title: "PR-only changes",
    description:
      "Infralyst never deploys directly to your infrastructure. All changes are delivered as Terraform pull requests in your own repos, and your team reviews, tests and merges them.",
  },
  {
    icon: ShieldCheck,
    title: "Data handled carefully",
    description:
      "We focus on infrastructure metadata and usage, not your application data. Data is encrypted in transit and at rest, and access to production systems is strictly limited.",
  },
] as const;

export function Security(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Security, privacy and <span className="text-primary">control</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          Infralyst is built for teams who care about their AWS accounts and
          Terraform repos. Here's how we keep it that way.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {SECURITY_POINTS.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-none">
                  <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="size-6" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{title}</h3>
                  <p className="text-surface-foreground/80 text-sm leading-relaxed">
                    {description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-sm text-surface-foreground/60 text-center mt-8">
          Want the full details?{" "}
          <Link href="/security" className="text-primary hover:underline">
            View security details
          </Link>
        </p>
      </div>
    </section>
  );
}
